import React from 'react';
import { z } from 'zod';
import { useMyForm } from '../../../../../../react-commons/hooks/useMyForm';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    SystemUserGroupRoleAssignmentMutationResourceSchema,
    SystemUserGroupRoleAssignmentPropertyValidation,
    SystemUserGroupRoleAssignmentResource,
    useSystemUserGroupRoleAssignmentActions,
} from '../../../../resource/resources/persistables/user/systemUserGroupRoleAssignmentResource';
import { SystemUserRoleResource } from '../../../../resource/resources/persistables/user/systemUserRole';
import { useSystemInformationActions } from '../../../../resource/services/systemInformationService';
import { Form_AutocompleteResourceTextField } from '../../../reusable-components/base/reference-text-fields/Form_AutocompleteResourceTextField';
import { EditChildBaseDialog } from '../../../reusable-components/dialogs/EditChildBaseDialog';
import { Form_DisplayComponentBaseView } from '../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { handleMutationResult } from '../../../util/utilFunctions';

export const AddOrUpdateSystemUserGroupRoleAssignmentDialog = ({
    onClose,
    systemUserRoleAssignment,
    roles,
}: {
    systemUserRoleAssignment: SystemUserGroupRoleAssignmentResource;
    roles: SystemUserRoleResource[];
    onClose: () => void;
}) => {
    const systemUserGroupRoleAssignmentActions = useSystemUserGroupRoleAssignmentActions();

    const addRoleAssignment = systemUserGroupRoleAssignmentActions.useAddResource();
    const updateRoleAssignment = systemUserGroupRoleAssignmentActions.useUpdateResource();

    const propertyDetails = useSystemInformationActions().useGetPropertyDetails(
        SystemUserGroupRoleAssignmentPropertyValidation.SYSTEM_USER_ROLES,
        ResourceType.SYSTEM_USER_GROUP_ROLE_ASSIGNMENT
    );

    const { form, formNames, onSubmit } = useMyForm({
        schema: SystemUserGroupRoleAssignmentMutationResourceSchema.extend({ selectedRole: z.string() }),
        defaultValues: { ...systemUserRoleAssignment, selectedRole: '' },
        submitHandler: async (data) => {
            if (!data.selectedRole) {
                return;
            }
            if (systemUserRoleAssignment.id !== '') {
                const roleAssignment = { ...systemUserRoleAssignment };
                roleAssignment.systemUserRoles.push(data.selectedRole);
                updateRoleAssignment.mutate(
                    {
                        resource: roleAssignment,
                        id: roleAssignment.id,
                    },
                    {
                        onSuccess: handleMutationResult(onClose),
                    }
                );
            } else {
                const roleAssignment = { ...systemUserRoleAssignment };
                roleAssignment.systemUserRoles.push(data.selectedRole);
                addRoleAssignment.mutate(roleAssignment, {
                    onSuccess: handleMutationResult(onClose),
                });
            }
        },
    });

    return (
        <EditChildBaseDialog
            edited={form.formState.isDirty}
            onClose={onClose}
            title={'Add Role Assignment to System User Group'}
            resourceType={ResourceType.SYSTEM_USER_GROUP_ROLE_ASSIGNMENT}
        >
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <Form_AutocompleteResourceTextField
                    label={'Role'}
                    control={form.control}
                    name={formNames.selectedRole}
                    nullable={false}
                    readOnly={false}
                    resources={roles.filter((value) => !systemUserRoleAssignment?.systemUserRoles.includes(value.id))}
                    onChange={(id) => {
                        form.setValue('selectedRole', id ?? '');
                    }}
                    labelAccessor={'name'}
                    propertyDetails={propertyDetails}
                    idSelector={'id'}
                />
            </Form_DisplayComponentBaseView>
        </EditChildBaseDialog>
    );
};
